import { Fragment, useEffect, useMemo, useState } from "react";
import { Grid } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import Layout from "components/layout/Layout";
import { OrderingPageStyles } from "./OrderingPage.styles";
import OrderKitMemberInformation from "./OrderKitMemberInformation";
import { PATHS } from "constants/pathConstants";
import PageTitle from "utils/PageTitle";
import Mixpanel from "utils/mixpanel";
import {
  setKitsSelectedToBeOrdered,
  setExistingOrders,
  setUuidsOfKitsJustOrderedToBeShownOnConclusionScreen,
  setHasPartialOrderFailure,
  selectExistingOrders,
  selectKitsSelectedToBeOrdered,
  selectTestKitsGLIsCurrentlyOffering
} from "store/reducers/order/OrderInformationSlice";
import {
  getMemberProfile,
  patchMemberProfileWithoutAddress
} from "apis/v3/memberApi";
import {
  selectUserPersonalInformation,
  updateFieldsInUserPersonalInformation
} from "store/reducers/user/UserAccountSlice";
import { openGLSnackbar } from "store/reducers/snackbar/openGLSnackbarSlice";
import { requestOrder } from "apis/ordersApi";
import LoadingPage from "components/v3/pages/LoadingPage";
import { TEST_TYPE_BY_UUID } from "components/v2/Pages/TestResultsPage/testLanguage";

export default function OrderingPage() {
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = OrderingPageStyles();

  const kitsSelectedToBeOrdered = useSelector(selectKitsSelectedToBeOrdered);
  const memberInformation = useSelector(selectUserPersonalInformation);
  const previouslyExistingKitOrders = useSelector(selectExistingOrders);
  const testKitsGLIsCurrentlyOffering = useSelector(
    selectTestKitsGLIsCurrentlyOffering
  );
  const [isProccessing, setIsProccessing] = useState(false);
  const hasOrdersSelected = kitsSelectedToBeOrdered?.length > 0;
  const testKitsGLIsCurrentlyOfferingMap = useMemo(() => {
    return testKitsGLIsCurrentlyOffering.reduce((prev, curr) => {
      return {
        ...prev,
        [curr.test_uuid]: {
          ...curr,
          test_type: curr.glTestType
        }
      };
    }, {});
  }, [testKitsGLIsCurrentlyOffering]);

  useEffect(() => {
    if (!hasOrdersSelected) {
      history.push(PATHS.ORDER_KIT_SELECTION);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasOrdersSelected]);

  const patchMemberProfile = async (
    formFields,
    suppressSuccessSnackbar = false
  ) => {
    patchMemberProfileWithoutAddress(formFields)
      .then((response) => {
        if (response.status === 200) {
          // Only show success snackbar if not suppressed (e.g., when order fails we show order-specific snackbar)
          if (!suppressSuccessSnackbar) {
            dispatch(
              openGLSnackbar({
                variant: "success",
                header: "Success",
                subText: "Member Information was saved successfully!"
              })
            );
          }
          getMemberProfile().then(({ data }) => {
            dispatch(updateFieldsInUserPersonalInformation(data));
          });
          return true;
        } else {
          dispatch(
            openGLSnackbar({
              variant: "error",
              header: "Error",
              subText:
                "Sorry, something went wrong. Please try again and reach out to us if you keep having trouble."
            })
          );
        }
      })
      .catch((error) => {
        dispatch(
          openGLSnackbar({
            variant: "error",
            header: "Error",
            subText:
              "Sorry, something went wrong. Please try again and reach out to us if you keep having trouble."
          })
        );
        return false;
      });
  };

  const handleSubmit = async (formFields) => {
    try {
      setIsProccessing(true);
      // Don't show success snackbar for member profile update - we'll show appropriate snackbar after order processing
      await patchMemberProfile(formFields, true);
      // Don't set this here - we'll set it only when orders succeed
      // Merge formFields with memberInformation, prioritizing formFields for email/phone
      // since those are validated in the form. Other fields like firstName, lastName, dob
      // come from memberInformation (Redux store) as they're not editable in this form.
      const mergedMemberData = {
        ...memberInformation,
        ...formFields
      };
      const memberPersonalInfo = (({
        email,
        dob,
        firstName,
        middleName,
        lastName,
        gender,
        phone,
        preferredName,
        pronouns,
        sexAssignedAtBirth,
        suffix
      }) => ({
        email,
        dob,
        firstName,
        middleName,
        lastName,
        gender,
        phoneNumber: phone?.slice(2),
        countryCode: phone?.slice(0, 2),
        preferredName,
        pronouns,
        sexAssignedAtBirth,
        suffix
      }))(mergedMemberData);
      const {
        addressLine1,
        addressLine2,
        city,
        country = "United States of America",
        postalCode,
        state
      } = formFields;
      const shippingInformation = {
        addressLine1,
        addressLine2,
        city,
        country,
        postalCode,
        state
      };
      const totalOrdersAttempted = kitsSelectedToBeOrdered.length;
      const newlyOrderedKits = await Promise.all(
        kitsSelectedToBeOrdered.map(async (kitUUIDToOrder) => {
          const currentTestInfo =
            testKitsGLIsCurrentlyOfferingMap?.[kitUUIDToOrder] || {};
          const { test_type: currentTestType } = currentTestInfo;
          const requestPayload = {
            memberData: memberPersonalInfo,
            shippingAddress: shippingInformation
          };

          // Include testType if available
          if (currentTestType) {
            requestPayload.testType = currentTestType;
          }

          // Include testId (the test_uuid) if available
          // This is needed for tests like AccessDx PGx where we need both testType and testId
          if (kitUUIDToOrder) {
            requestPayload.testId = kitUUIDToOrder;
          }

          try {
            const response = await requestOrder(requestPayload);

            // Handle both AxiosResponse and AxiosError
            // AxiosResponse has status directly
            // AxiosError (from doApiCall catch) has response.response.status
            // Check if response is an AxiosError (has isAxiosError property)
            const isError = response?.isAxiosError === true;
            const statusCode = isError
              ? response?.response?.status
              : response?.status;

            if (statusCode === 201) {
              Mixpanel.track(`Kit order ${kitUUIDToOrder} successful`);
              return {
                description: {
                  name: TEST_TYPE_BY_UUID[kitUUIDToOrder],
                  uuid: kitUUIDToOrder
                },
                documentIds: [],
                glOrderId: `${kitUUIDToOrder}-temp`,
                status: "active"
              };
            } else {
              // Non-201 status code or error - order failed
              Mixpanel.track(
                `Kit order ${kitUUIDToOrder} failed with status: ${statusCode}`
              );
              return null; // Return null in case of failure to filter it later
            }
          } catch (error) {
            // If requestOrder throws an error (unexpected), treat it as a failed order
            Mixpanel.track(`Kit order ${kitUUIDToOrder} failed with error`);
            return null;
          }
        })
      );

      // Filter out any null entries (failed orders). Can't seem to make reduce
      //  wait properly.
      const validOrderedKits = (
        newlyOrderedKits.filter((kit) => kit !== null) || []
      ).map((kit) => {
        return {
          ...testKitsGLIsCurrentlyOfferingMap?.[kit?.description?.uuid],
          ...kit
        };
      });

      const successfulOrdersCount = validOrderedKits.length;
      const failedOrdersCount = totalOrdersAttempted - successfulOrdersCount;

      // Determine scenario and handle accordingly
      if (failedOrdersCount === totalOrdersAttempted) {
        // All orders failed - stay on page with error snackbar
        // Don't clear selected kits - keep them so user can fix and retry
        dispatch(
          openGLSnackbar({
            variant: "error",
            header: true,
            subText:
              "Sorry, your submission failed. Please try again, and reach out to us if you keep having trouble."
          })
        );
        setIsProccessing(false);
        // Don't redirect - stay on the order page
        // Don't clear kitsSelectedToBeOrdered to avoid triggering useEffect redirect
      } else if (failedOrdersCount === 0) {
        // All orders succeeded - go to success page
        // Clear partial failure flag
        dispatch(setHasPartialOrderFailure(false));
        dispatch(
          openGLSnackbar({
            variant: "success",
            header: "Success",
            subText: "The kit was requested successfully!"
          })
        );
        dispatch(
          setUuidsOfKitsJustOrderedToBeShownOnConclusionScreen(
            validOrderedKits.map((kit) => kit.description.uuid)
          )
        );
        dispatch(
          setExistingOrders({
            data: {
              orders: [...previouslyExistingKitOrders, ...validOrderedKits]
            }
          })
        );
        dispatch(setKitsSelectedToBeOrdered([]));
        setIsProccessing(false);
        history.push(PATHS.ORDER_KIT_CONFIRMATION);
      } else {
        // Some orders failed, some succeeded - go to success page but show error snackbar
        dispatch(
          openGLSnackbar({
            variant: "error",
            header: true,
            subText:
              "The order for some of the kits you requested failed to process, please try again from the dashboard."
          })
        );
        // Set flag to indicate partial failure
        dispatch(setHasPartialOrderFailure(true));
        // Only show successful orders in the confirmation page
        dispatch(
          setUuidsOfKitsJustOrderedToBeShownOnConclusionScreen(
            validOrderedKits.map((kit) => kit.description.uuid)
          )
        );
        dispatch(
          setExistingOrders({
            data: {
              orders: [...previouslyExistingKitOrders, ...validOrderedKits]
            }
          })
        );
        dispatch(setKitsSelectedToBeOrdered([]));
        setIsProccessing(false);
        history.push(PATHS.ORDER_KIT_CONFIRMATION);
      }
    } catch (error) {
      setIsProccessing(false);
      let errorDescription = "";

      if (error.response.status === 409) {
        errorDescription =
          "We're sorry, but we were unable to process your order. Please try again, and reach out to us if you keep having trouble.";
      }
      dispatch(
        openGLSnackbar({
          variant: "error",
          header: "We're sorry...",
          subText: errorDescription
        })
      );
      return null; // Return null in case of an error to filter it later
    }
  };

  PageTitle("Member information");

  return (
    <Fragment>
      <Layout>
        <Grid className={classes.boxStepper}>
          {isProccessing ? (
            <LoadingPage message="Processing your request ..." />
          ) : (
            <OrderKitMemberInformation handleSubmit={handleSubmit} />
          )}
        </Grid>
      </Layout>
    </Fragment>
  );
}
